import * as React from 'react';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';

import { Button } from '@common/components/Button';
import { FormControl } from '@common/components/form-control';
import { Flex } from '@common/components/Flex';

import { LOOKUP_MODULE_NAME } from '../../LookupTable';

import './DataExport.styles.scss';

const SETTING_OTHER_TYPE = 'TXT_OTHER';
const DEFAULT_DELIMITER = ',';

export const DataExport = (props) => {
  const { close, onExport } = props;

  const settings = React.useMemo(() => {
    return [
      { label: 'Text (Tab Delimited)', id: 'TXT_TAB' },
      { label: 'Text (Comma Delimited)', id: 'TXT_COMMA' },
      { label: 'Text (Other)', id: SETTING_OTHER_TYPE },
      { label: 'Excel (xls)', id: 'XLS' },
      { label: 'Excel (xlsx)', id: 'XLSX' },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const options = React.useMemo(() => {
    return [
      { label: 'Include Column Headings', metadata: { showHeaders: true } },
    ].map((x) => ({ ...x, id: x.label, label: I18n.t(x.label) }));
  }, []);

  const [selectedSetting, setSelectedSetting] = React.useState(settings[0].id);
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [delimiter, setDelimiter] = React.useState(DEFAULT_DELIMITER);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSettingsChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    setSelectedSetting(value);

    setDelimiter(value === SETTING_OTHER_TYPE ? '' : DEFAULT_DELIMITER);
  }, [selectedSetting]);

  const handleOptionsChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    const resultSettings = selectedOptions.includes(value)
      ? selectedOptions.filter((x) => x !== value)
      : [...selectedOptions, value];

    setSelectedOptions(resultSettings);
  }, [selectedOptions]);

  const handleDelimiterChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    setDelimiter(value);
  }, []);

  const handleExport = React.useCallback(async () => {
    try {
      setSubmitting(true);

      const optionsSubmitData = selectedOptions.reduce((acc, id) => {
        const { metadata } = options.find((x) => x.id === id);

        return {
          ...acc,
          ...metadata
        };
      }, {});

      await onExport({
        delimiter,
        exportType: selectedSetting,
        ...optionsSubmitData,
      });
    } catch (err) {
      setSubmitting(false);
      throw err;
    }
  }, [onExport, options, selectedOptions, selectedSetting, delimiter]);

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="DataExport"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close} className="DataExport__header">
        <ModalTitle id={`${LOOKUP_MODULE_NAME}-export-data-header`}>
          {I18n.t('Export Data')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody className="DataExport__content">
        <Flex alignItems="flex-start">
          <div className="DataExport__content__sections">
            <section className="DataExport__content__section">
              <span className="DataExport__content__section__title">{I18n.t('Settings')}</span>

              {settings.map((x) => (
                <Flex key={x.id} alignItems="center" justify="space-between">
                  <label className="DataExport__content__section__item">
                    <input 
                      id={`${LOOKUP_MODULE_NAME}-export-setting-${x.id}`} 
                      type="radio" 
                      value={x.id}
                      checked={selectedSetting === x.id}
                      name={`${LOOKUP_MODULE_NAME}-export-setting`} 
                      onChange={handleSettingsChange} 
                    />
                    {x.label}
                  </label>
                  {x.id === selectedSetting && x.id === SETTING_OTHER_TYPE && (
                    <Flex className="DataExport__content__delimiter" alignItems="center">
                      <span>{I18n.t('Delimiter')}:&nbsp;</span>
                      <FormControl
                        value={delimiter}
                        onChange={handleDelimiterChange}
                      />
                    </Flex>
                  )}
                </Flex>
              ))}
            </section>

            <section className="DataExport__content__section">
              <span className="DataExport__content__section__title">{I18n.t('Options')}</span>

              {options.map((x) => (
                <label key={x.id} className="DataExport__content__section__item">
                  <input 
                    id={`${LOOKUP_MODULE_NAME}-export-option-${x.id}`} 
                    type="checkbox" 
                    value={x.id}
                    checked={selectedOptions.includes(x.id)}
                    name={`${LOOKUP_MODULE_NAME}-export-option`} 
                    onChange={handleOptionsChange} 
                  />
                  {x.label}
                </label>
              ))}
            </section>
          </div>
          <Button 
            className="DataExport__content__submit-btn"
            disabled={!delimiter || submitting}
            id={`${LOOKUP_MODULE_NAME}-export-button`} 
            onClick={handleExport}
          >
            {submitting ? I18n.t('Exporting') : I18n.t('Export')}
          </Button>
        </Flex>
      </ModalBody>
    </Modal>
  );
};